/**
 * useDetailKeyboardShortcuts — Keyboard shortcuts for the Entity Detail Panel
 *
 * Shortcuts (when panel is open):
 *   1-9: Switch to tab by position
 *   D: Details tab
 *   E: Edit tab
 *   R: Relationships tab
 *   A: Audit tab
 *   T: Timeline tab
 *   V: Versions tab
 *   F: Toggle fullscreen
 *   Esc: Close panel (or exit fullscreen)
 *   ?: Show shortcut overlay
 *   Arrow Up/Down: Row navigation (handled by useRowNavigation)
 */

import { useEffect, useState, useCallback } from 'react';
import type { DetailTab } from './types';

// ============================================================================
// SHORTCUT MAP
// ============================================================================

export interface ShortcutEntry {
  key: string;
  label: string;
  description: string;
}

export const SHORTCUT_MAP: ShortcutEntry[] = [
  { key: 'D', label: 'D', description: 'Details tab' },
  { key: 'E', label: 'E', description: 'Edit tab' },
  { key: 'R', label: 'R', description: 'Relationships tab' },
  { key: 'A', label: 'A', description: 'Audit Trail tab' },
  { key: 'T', label: 'T', description: 'Timeline tab' },
  { key: 'V', label: 'V', description: 'Versions tab' },
  { key: 'C', label: 'C', description: 'Compare tab' },
  { key: 'F', label: 'F', description: 'Toggle fullscreen' },
  { key: 'Escape', label: 'Esc', description: 'Close panel' },
  { key: '?', label: '?', description: 'Show shortcuts' },
  { key: 'ArrowUp', label: '↑', description: 'Previous row' },
  { key: 'ArrowDown', label: '↓', description: 'Next row' },
  { key: 'Ctrl+ArrowUp', label: 'Ctrl+↑', description: 'First row' },
  { key: 'Ctrl+ArrowDown', label: 'Ctrl+↓', description: 'Last row' },
];

/** Keyboard key → tab mapping */
const KEY_TO_TAB: Record<string, DetailTab> = {
  d: 'details',
  e: 'edit',
  r: 'relationships',
  a: 'audit',
  t: 'timeline',
  v: 'versions',
  c: 'compare',
};

/** Number key → tab index position */
const NUM_TO_TAB_INDEX: Record<string, number> = {
  '1': 0,
  '2': 1,
  '3': 2,
  '4': 3,
  '5': 4,
  '6': 5,
  '7': 6,
  '8': 7,
  '9': 8,
};

// ============================================================================
// HOOK
// ============================================================================

interface UseDetailKeyboardShortcutsOptions {
  /** Whether the panel is currently open */
  panelOpened: boolean;
  /** Available tabs (in order) */
  visibleTabs: DetailTab[];
  /** Set the active tab */
  setActiveTab: (tab: DetailTab) => void;
  /** Toggle fullscreen */
  toggleFullscreen: () => void;
  /** Whether panel is in fullscreen mode */
  isFullscreen: boolean;
  /** Close the panel */
  onClose: () => void;
}

export function useDetailKeyboardShortcuts({
  panelOpened,
  visibleTabs,
  setActiveTab,
  toggleFullscreen,
  isFullscreen,
  onClose,
}: UseDetailKeyboardShortcutsOptions) {
  const [showOverlay, setShowOverlay] = useState(false);

  const toggleOverlay = useCallback(() => {
    setShowOverlay(prev => !prev);
  }, []);

  const closeOverlay = useCallback(() => {
    setShowOverlay(false);
  }, []);

  useEffect(() => {
    if (!panelOpened) {
      setShowOverlay(false);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      const key = e.key.toLowerCase();

      // ? = toggle shortcut overlay
      if (e.key === '?') {
        e.preventDefault();
        toggleOverlay();
        return;
      }

      // Esc = close overlay first, then exit fullscreen, then close panel
      if (e.key === 'Escape') {
        if (showOverlay) {
          setShowOverlay(false);
          return;
        }
        if (isFullscreen) {
          toggleFullscreen();
          return;
        }
        onClose();
        return;
      }

      // F = toggle fullscreen
      if (key === 'f' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        toggleFullscreen();
        return;
      }

      // Letter keys → tab switching
      const tabFromKey = KEY_TO_TAB[key];
      if (tabFromKey && visibleTabs.includes(tabFromKey)) {
        e.preventDefault();
        setActiveTab(tabFromKey);
        return;
      }

      // Number keys → tab by position
      const tabIndex = NUM_TO_TAB_INDEX[e.key];
      if (tabIndex !== undefined && tabIndex < visibleTabs.length) {
        e.preventDefault();
        const tab = visibleTabs[tabIndex];
        if (tab) setActiveTab(tab);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [panelOpened, visibleTabs, setActiveTab, toggleFullscreen, isFullscreen, onClose, showOverlay, toggleOverlay]);

  return {
    showOverlay,
    toggleOverlay,
    closeOverlay,
  };
}
